import * as React from 'react';

import { Button, ButtonProps } from '@mui/material';

import { uiButtonSx, type CustomButtonVariant, type CustomButtonSize } from '../config';

import type { SxProps, Theme } from '@mui/material/styles';

type Props = Omit<ButtonProps, 'variant' | 'href' | 'component'> & {
	variantType?: CustomButtonVariant;
	size?: CustomButtonSize;
	href?: string;
	target?: React.HTMLAttributeAnchorTarget;
	rel?: string;
	component?: React.ElementType;
};

export default function CustomButton({
	variantType = 'primary',
	size = 'medium',
	href,
	target,
	rel,
	sx,
	component,
	...props
}: Props) {
	let customSx: SxProps<Theme>[] = [];
	if (Array.isArray(sx)) {
		customSx = sx as SxProps<Theme>[];
	} else if (sx) {
		customSx = [sx as SxProps<Theme>];
	}

	const sxArray: SxProps<Theme>[] = [
		uiButtonSx.base,
		uiButtonSx.size[size],
		uiButtonSx.variant[variantType],
		...customSx,
	];

	// ✅ anchor overload: only pass href when it is a string
	if (typeof href === 'string' && href.length > 0) {
		return (
			<Button
				disableElevation
				component="a"
				href={href}
				target={target}
				rel={rel ?? (target === '_blank' ? 'noreferrer noopener' : undefined)}
				sx={sxArray as SxProps<Theme>}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				{...(props as any)}
			/>
		);
	}

	// ✅ component overload: only pass component when it's defined
	if (component) {
		return <Button disableElevation {...props} component={component} sx={sxArray as SxProps<Theme>} />;
	}

	// ✅ default overload (button)
	return <Button disableElevation {...props} sx={sxArray as SxProps<Theme>} />;
}
